/*
 * nuauge-infinite-scroll
 * ----------------------------------------------------------------------------
 * Carrega automaticamente as próximas páginas da grade de produtos da coleção
 * conforme o cliente rola a página, anexando os itens à grade atual em vez de
 * quebrar o catálogo em páginas separadas.
 *
 * Fica dentro de #ProductGridContainer, portanto é recriado automaticamente
 * quando o facets.js substitui o HTML da grade (ao aplicar filtros / ordenação),
 * voltando a começar da página 1.
 */
class NuaugeInfiniteScroll extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';
    this.nextUrl = this.buildUrl((this.dataset.nextUrl || '').trim());
    this.loading = false;
    this.pagesLoaded = 0;
    this.maxPages = parseInt(this.dataset.maxPages, 10) || 100;
    this.grid = document.getElementById('product-grid');
    this.sentinel = this.querySelector('.nuauge-infinite-scroll__sentinel');
    this.spinner = this.querySelector('.nuauge-infinite-scroll__spinner');

    if (!this.nextUrl || !this.grid || !this.sentinel || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
      },
      { rootMargin: '800px 0px' }
    );
    this.observer.observe(this.sentinel);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  // Garante que a URL buscada renderize apenas esta seção (fetch mais leve).
  buildUrl(rawUrl) {
    if (!rawUrl) return '';
    try {
      const url = new URL(rawUrl, window.location.origin);
      if (this.sectionId) url.searchParams.set('section_id', this.sectionId);
      return url.pathname + url.search;
    } catch (error) {
      return rawUrl;
    }
  }

  async loadNext() {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.pagesLoaded += 1;
    if (this.spinner) this.spinner.classList.remove('hidden');

    try {
      const response = await fetch(this.nextUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status} ao buscar ${this.nextUrl}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = doc.querySelectorAll('#product-grid > li.grid__item');

      if (items.length > 0) {
        const fragment = document.createDocumentFragment();
        items.forEach((item) => {
          const clone = document.importNode(item, true);
          // Os itens anexados aparecem imediatamente: removemos os gatilhos de
          // animação "reveal on scroll" para não ficarem invisíveis.
          clone.classList.remove('scroll-trigger', 'animate--slide-in');
          clone.removeAttribute('data-cascade');
          clone.style.removeProperty('--animation-order');
          clone
            .querySelectorAll('.scroll-trigger')
            .forEach((child) => child.classList.remove('scroll-trigger', 'animate--slide-in'));
          fragment.appendChild(clone);
        });
        this.grid.appendChild(fragment);
      }

      // Descobre a URL da página seguinte. A grade renderizada volta a incluir
      // <nuauge-infinite-scroll data-next-url="..."> (vazio na última página).
      // A paginação numerada fica dentro de <noscript>, que o DOMParser não
      // expõe como DOM, por isso ela é apenas um fallback.
      const fetchedWidget = doc.querySelector('nuauge-infinite-scroll');
      let rawNext = fetchedWidget ? (fetchedWidget.getAttribute('data-next-url') || '') : '';
      if (!rawNext) {
        const nextLink =
          doc.querySelector('.pagination__item--prev') ||
          doc.querySelector('a.pagination__item[rel="next"]');
        rawNext = nextLink ? nextLink.getAttribute('href') : '';
      }
      this.nextUrl = this.buildUrl(rawNext.trim());
      this.dataset.nextUrl = this.nextUrl || '';

      if (!this.nextUrl || items.length === 0 || this.pagesLoaded >= this.maxPages) {
        this.observer.disconnect();
      }
    } catch (error) {
      // Em caso de erro, paramos de observar para não repetir a falha em loop.
      // A navegação por URL (?page=N) continua funcionando normalmente.
      console.error('[nuauge-infinite-scroll]', error);
      if (this.observer) this.observer.disconnect();
    } finally {
      this.loading = false;
      if (this.spinner) this.spinner.classList.add('hidden');
    }

    // O IntersectionObserver só notifica ao cruzar o limite. Se o sentinela
    // continuar perto da viewport após anexar os itens, seguimos carregando.
    if (this.nextUrl && this.pagesLoaded < this.maxPages) {
      requestAnimationFrame(() => {
        if (!this.isConnected || this.loading || !this.sentinel) return;
        const rect = this.sentinel.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 800) this.loadNext();
      });
    }
  }
}

if (!customElements.get('nuauge-infinite-scroll')) {
  customElements.define('nuauge-infinite-scroll', NuaugeInfiniteScroll);
}
